/**
 * Recognising a stale-chunk failure, in any browser.
 *
 * BS-1169 context: every page in this app is a lazy `import()`. After a deploy
 * the HTML a tab is still running references the PREVIOUS build's asset
 * hashes, and nginx serves `/assets/*` from a regex `location` with no
 * try_files fallback — so the old chunk is a real 404 and the navigation
 * rejects. The router answers that by reloading once; it only ever did so for
 * the Chromium and Safari wordings, which left Firefox users on the "page
 * could not be found" screen with no way out but a manual refresh. Mat AI is
 * one of the largest chunks in the build and sits behind a single nav click,
 * so it is where this is most visible.
 *
 * Matched on the MESSAGE because there is no shared error type: the spec says
 * only that the import rejects, and each engine words it differently.
 *
 *   Chromium  "Failed to fetch dynamically imported module: <url>"
 *   Firefox   "error loading dynamically imported module: <url>"
 *   Safari    "Importing a module script failed."
 *   webpack   err.name === 'ChunkLoadError'
 *
 * The two MIME variants are the same failure seen through a server that
 * answers a missing asset with index.html instead of a 404 — the browser then
 * refuses the response as the wrong type rather than reporting a 404.
 */
const CHUNK_ERROR_PATTERNS = [
    'failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'importing a module script failed',
    'unable to preload css',
    // Served HTML where a module was expected.
    'is not a valid javascript mime type',
    'expected a javascript module script'
];

export function isChunkLoadError(error: unknown): boolean {
    if (!error) return false;
    const e = error as { message?: unknown; name?: unknown };
    if (e.name === 'ChunkLoadError') return true;
    const message = typeof e.message === 'string' ? e.message.toLowerCase() : '';
    if (!message) return false;
    return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

const RELOAD_KEY = 'chunk-reload-ts';
const RELOAD_COOLDOWN_MS = 10000;

/**
 * Reload once onto `target` to pick up the new build. Returns false when a
 * reload already happened inside the cooldown — the chunk is missing for some
 * other reason and reloading again would spin.
 */
export function reloadForChunkError(target: string, now: number = Date.now()): boolean {
    const last = sessionStorage.getItem(RELOAD_KEY);
    if (last && now - Number(last) <= RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(now));
    window.location.assign(target);
    return true;
}
